// ─────────────────────────────────────────────────────────────────────────────
// HCEN · Super Admin · Refer & Earn dashboard
//
// Hits /admin/referrals on HCEN_Admin_API (same gateway as the rest of the panel).
//
// Design (May 17, 2026):
//   • 6-card summary at top: Total / Signed Up / Booked / Credited / Coupons / ₹ Earned
//   • Top referrers leaderboard (descending by credited)
//   • Recent 50 referrals table with status colour chips
// ─────────────────────────────────────────────────────────────────────────────

import React, { useEffect, useState, useCallback } from 'react';
import { adminReferralsAPI } from '../../services/api';

const STATUS_COLOUR = {
  pending:     { bg: '#374151', fg: '#d1d5db', label: 'Pending' },
  signed_up:   { bg: '#1e3a5f', fg: '#93c5fd', label: 'Signed Up' },
  booked:      { bg: '#3a2e1e', fg: '#fbbf24', label: 'Booked' },
  credited:    { bg: '#1e3b2c', fg: '#6ee7b7', label: 'Credited ✓' },
  cap_reached: { bg: '#3b1e3a', fg: '#f0abfc', label: 'Cap Reached' },
  expired:     { bg: '#3b1e1e', fg: '#fca5a5', label: 'Expired' },
};

export default function AdminReferrals({ onBack, embedded = false }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await adminReferralsAPI.list();
      if (r.error) throw new Error(r.error);
      setData(r.data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div style={S.center}>Loading referrals…</div>;
  if (error)   return (
    <div style={{ ...S.page, padding: 32 }}>
      <button style={S.backBtn} onClick={onBack}>← Back</button>
      <div style={S.errCard}>
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>Could not load</div>
        <div style={{ fontSize: 13, color: '#fca5a5' }}>{error}</div>
        <button style={{ ...S.primaryBtn, marginTop: 16 }} onClick={load}>Retry</button>
      </div>
    </div>
  );

  const summary = data?.summary || {};
  const leaderboard = Array.isArray(data?.leaderboard) ? data.leaderboard : [];
  const recent = Array.isArray(data?.recent) ? data.recent : [];
  return (
    <div style={{ ...S.page, ...(embedded ? { minHeight: 'auto', padding: 0, background: 'transparent' } : {}) }}>
      <div style={S.headerBar}>
        {!embedded && <button style={S.backBtn} onClick={onBack}>← Back</button>}
        <div style={S.title}>Refer &amp; Earn — Admin Dashboard</div>
        <button style={S.refreshBtn} onClick={load}>↻ Refresh</button>
      </div>

      {/* ── Summary cards ─────────────────────────────────────────── */}
      <div style={S.grid}>
        <Stat label="Total Referrals" value={summary.total ?? 0} accent="#60a5fa" />
        <Stat label="Signed Up"       value={summary.signed_up ?? 0} accent="#93c5fd" />
        <Stat label="Booked"          value={summary.booked ?? 0} accent="#fbbf24" />
        <Stat label="Credited ✓"      value={summary.credited ?? 0} accent="#6ee7b7" />
        <Stat label="Coupons Issued"  value={summary.totalCouponsIssued ?? 0} accent="#f472b6" />
        <Stat label="Total Paid Out"  value={`₹${summary.totalEarnedINR ?? 0}`} accent="#34d399" />
      </div>

      {/* ── Leaderboard ───────────────────────────────────────────── */}
      <div style={S.section}>
        <div style={S.sectionTitle}>🏆 Top Referrers</div>
        {leaderboard.length === 0
          ? <div style={S.empty}>No credited referrals yet.</div>
          : (
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>#</th>
                  <th style={S.th}>Code</th>
                  <th style={S.th}>Phone</th>
                  <th style={S.th}>Invited</th>
                  <th style={S.th}>Credited</th>
                  <th style={S.th}>Earned</th>
                </tr>
              </thead>
              <tbody>
                {leaderboard.map((r, i) => (
                  <tr key={r.referrerUsersId} style={S.tr}>
                    <td style={S.td}>{i + 1}</td>
                    <td style={{ ...S.td, fontFamily: 'monospace', color: '#fbbf24', fontWeight: 700 }}>{r.referrerCode}</td>
                    <td style={S.td}>{r.referrerPhone || '—'}</td>
                    <td style={S.td}>{r.invited}</td>
                    <td style={{ ...S.td, color: '#6ee7b7', fontWeight: 700 }}>{r.credited}</td>
                    <td style={{ ...S.td, color: '#34d399', fontWeight: 700 }}>₹{r.earned}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        }
      </div>

      {/* ── Recent referrals ──────────────────────────────────────── */}
      <div style={S.section}>
        <div style={S.sectionTitle}>📋 Recent Referrals</div>
        {recent.length === 0
          ? <div style={S.empty}>No referrals tracked yet.</div>
          : (
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Code</th>
                  <th style={S.th}>Referrer</th>
                  <th style={S.th}>Referee</th>
                  <th style={S.th}>Status</th>
                  <th style={S.th}>Created</th>
                  <th style={S.th}>Credited</th>
                </tr>
              </thead>
              <tbody>
                {recent.map(r => (
                  <tr key={r.referralId} style={S.tr}>
                    <td style={{ ...S.td, fontFamily: 'monospace', color: '#fbbf24' }}>{r.referrerCode}</td>
                    <td style={S.td}>{r.referrerPhone || '—'}</td>
                    <td style={S.td}>{r.refereePhone || '—'}</td>
                    <td style={S.td}><StatusChip status={r.status} /></td>
                    <td style={{ ...S.td, fontSize: 11, color: '#9ca3af' }}>{fmt(r.createdAt)}</td>
                    <td style={{ ...S.td, fontSize: 11, color: '#9ca3af' }}>{r.creditedAt ? fmt(r.creditedAt) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        }
      </div>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div style={S.statCard}>
      <div style={{ fontSize: 11, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700 }}>{label}</div>
      <div style={{ fontSize: 26, color: accent, fontWeight: 800, marginTop: 6 }}>{value}</div>
    </div>
  );
}

function StatusChip({ status }) {
  const c = STATUS_COLOUR[status] || { bg: '#374151', fg: '#d1d5db', label: status };
  return (
    <span style={{
      background: c.bg, color: c.fg,
      padding: '3px 10px', borderRadius: 12, fontSize: 11,
      fontWeight: 700, whiteSpace: 'nowrap',
    }}>{c.label}</span>
  );
}

function fmt(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) +
           ' · ' + d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
}

const S = {
  page: { background: '#0f172a', minHeight: '100vh', color: '#e5e7eb', padding: 24, fontFamily: 'Inter, system-ui, sans-serif' },
  center: { display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', color: '#9ca3af' },
  headerBar: { display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 },
  backBtn: { background: 'transparent', border: '1px solid #374151', color: '#e5e7eb', padding: '8px 14px', borderRadius: 8, cursor: 'pointer', fontWeight: 600 },
  refreshBtn: { background: '#1e3a5f', border: 'none', color: '#93c5fd', padding: '8px 14px', borderRadius: 8, cursor: 'pointer', fontWeight: 600, marginLeft: 'auto' },
  primaryBtn: { background: '#3b82f6', border: 'none', color: 'white', padding: '10px 18px', borderRadius: 8, cursor: 'pointer', fontWeight: 700 },
  title: { fontSize: 22, fontWeight: 800, color: '#f9fafb' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 14, marginBottom: 24 },
  statCard: { background: '#1e293b', borderRadius: 12, padding: 18, border: '1px solid #334155' },
  section: { background: '#1e293b', borderRadius: 12, padding: 20, border: '1px solid #334155', marginBottom: 18 },
  sectionTitle: { fontSize: 15, fontWeight: 800, color: '#f9fafb', marginBottom: 14 },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: { textAlign: 'left', padding: '10px 8px', borderBottom: '1px solid #334155', color: '#9ca3af', fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 },
  td: { padding: '10px 8px', borderBottom: '1px solid #1f2937', color: '#e5e7eb' },
  tr: { transition: 'background 120ms' },
  empty: { padding: 28, textAlign: 'center', color: '#6b7280', fontSize: 13 },
  errCard: { background: '#1f2937', borderRadius: 12, padding: 24, border: '1px solid #b91c1c' },
};
